
import React, { useState, useEffect, useMemo } from 'react';
import { Search, User, Newspaper, ArrowRight, Loader2, X, TrendingUp, Eye, ChevronRight, ChevronLeft } from 'lucide-react';
import { searchPlayers, searchNews, fetchPopularNews, getNewsViewCount } from '../../services/footballService';
import { useDebounce } from '../../hooks/useDebounce';
import { NewsItem } from '../../types';
import { useLanguage } from '../../context/LanguageContext';
import { XlevenLogo } from './XlevenLogo';
import { AppFooter } from './AppFooter';
import { WebtoonLogo } from './WebtoonLogo';
import { t } from '../../i18n';

interface SidebarSearchProps {
  onNavigateToPlayer: (id: string) => void;
  onNavigateToNews: (item: NewsItem) => void;
}

const getCountryCode = (nationality?: string) => {
    if (!nationality) return 'un';
    const map: Record<string, string> = {
        'Spain': 'es', 'England': 'gb-eng', 'Germany': 'de', 'France': 'fr',
        'Italy': 'it', 'Brazil': 'br', 'Argentina': 'ar', 'Portugal': 'pt',
        'Netherlands': 'nl', 'Belgium': 'be', 'Norway': 'no', 'Sweden': 'se',
        'Korea, South': 'kr', 'Korea Republic': 'kr', 'South Korea': 'kr', 'Japan': 'jp', 'USA': 'us', 'Poland': 'pl',
        'Turkey': 'tr', 'Morocco': 'ma', 'Croatia': 'hr', 'Denmark': 'dk',
        'Egypt': 'eg', 'Senegal': 'sn', 'Uruguay': 'uy', 'Colombia': 'co',
        'Nigeria': 'ng', 'Ghana': 'gh', 'Ivory Coast': 'ci', 'Cameroon': 'cm'
    };
    return map[nationality] || 'un';
};

export const SidebarSearch: React.FC<SidebarSearchProps> = ({ onNavigateToPlayer, onNavigateToNews }) => {
  const { lang } = useLanguage();
  const [query, setQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'player' | 'news'>('player');
  const [results, setResults] = useState<any[]>([]);
  const [popularNews, setPopularNews] = useState<NewsItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isPopularLoading, setIsPopularLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 7;
  
  const debouncedQuery = useDebounce(query, 400);

  useEffect(() => {
    const loadPopular = async () => {
      setIsPopularLoading(true);
      const news = await fetchPopularNews(lang, 10);
      const filtered = news.filter(n => n.source || n.user_id).slice(0, 5);
      setPopularNews(filtered);
      setIsPopularLoading(false);
    };
    loadPopular();
  }, [lang]);

  useEffect(() => {
    setCurrentPage(1);
    if (debouncedQuery.length >= (activeTab === 'player' ? 3 : 2)) {
      setIsSearching(true);
      const performSearch = async () => {
        try {
          const res = activeTab === 'player' 
            ? await searchPlayers(debouncedQuery) 
            : await searchNews(debouncedQuery, lang);
          
          const finalRes = activeTab === 'news' 
            ? (res || []).filter((n: any) => n.source || n.user_id) 
            : (res || []);

          setResults(finalRes);
        } catch (e) {
          setResults([]);
        } finally {
          setIsSearching(false);
        }
      };
      performSearch();
    } else {
      setResults([]);
      setIsSearching(false);
    }
  }, [debouncedQuery, activeTab, lang]);

  const hotspotTags = useMemo(() => {
      if (!popularNews || popularNews.length === 0) return [];
      const playersSet = new Set<string>();
      popularNews.forEach(news => {
          if (news.players && Array.isArray(news.players)) {
              news.players.forEach(p => {
                  if (p && p.trim()) playersSet.add(p.trim());
              });
          }
      });
      return Array.from(playersSet).slice(0, 10);
  }, [popularNews]);

  const handleTagClick = (tag: string) => {
      setQuery(tag);
      setActiveTab('player');
  };

  const totalPages = Math.ceil(results.length / itemsPerPage);
  const paginatedResults = results.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handleResultClick = (item: any) => {
    if (activeTab === 'player') {
      onNavigateToPlayer(item.id);
    } else {
      onNavigateToNews(item);
    }
    setQuery('');
  };

  return (
    <div className="flex flex-col gap-6 h-full">
      <div className="relative group">
        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-rose-500 transition-colors">
          <Search size={18} strokeWidth={2.5} />
        </div>
        <input 
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t(lang, activeTab === 'player' ? 'searchPlayerPlaceholder' : 'searchNewsPlaceholder')}
          className="w-full bg-gray-100 border-none rounded-2xl py-3.5 pl-12 pr-6 text-sm font-bold text-slate-900 focus:ring-2 focus:ring-rose-500/20 focus:bg-white transition-all placeholder:text-slate-400"
        />
        {query && (
          <button 
            onClick={() => setQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-slate-200 text-slate-500 rounded-full flex items-center justify-center md:hover:bg-slate-300 transition-all"
          >
            <X size={12} strokeWidth={3} />
          </button>
        )}
      </div>

      {query.length > 0 && (
        <div className="bg-gray-50 rounded-2xl overflow-hidden border border-gray-100 min-h-[100px] flex flex-col animate-in fade-in slide-in-from-top-2 shadow-sm max-h-[500px]">
            <div className="flex bg-gray-100/50 p-1 border-b border-gray-100 shrink-0">
                <button onClick={() => setActiveTab('player')} className={`flex-1 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all ${activeTab === 'player' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-400'}`}>Players</button>
                <button onClick={() => setActiveTab('news')} className={`flex-1 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all ${activeTab === 'news' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-400'}`}>News</button>
            </div>
            {isSearching ? (
              <div className="py-12 flex flex-col items-center justify-center gap-3 shrink-0">
                <Loader2 className="animate-spin text-rose-500" size={20} />
              </div>
            ) : paginatedResults.length > 0 ? (
              <>
                <div className="divide-y divide-gray-100 overflow-y-auto scrollbar-hide flex-1">
                  {paginatedResults.map((item, idx) => {
                    const sourceLower = item.source?.toLowerCase() || '';
                    const isPartnerSource = sourceLower.includes('bbc') || sourceLower.includes('teamtalk') || sourceLower.includes('si') || sourceLower.includes('sports illustrated');
                    const isWeeklyMagazine = sourceLower === 'magazine';
                    const isWebtoon = sourceLower === 'webtoon' && item.type === 'webtoon';
                    const isUserPost = !!item.user_id;
                    const displayName = isPartnerSource ? 'XLEVEN' : isWeeklyMagazine ? 'Magazine' : isWebtoon ? 'Webtoon' : (isUserPost ? item.author?.name : (item.source || null));

                    return (
                        <button
                          key={item.id || idx}
                          onClick={() => handleResultClick(item)}
                          className="w-full flex items-center gap-3 p-4 md:hover:bg-white md:hover:shadow-inner transition-all text-left group"
                        >
                          {activeTab === 'player' ? (
                            <>
                              <div className="w-10 h-10 rounded-full overflow-hidden bg-white border border-gray-100 shrink-0">
                                <img src={item.avatar} alt="" className="w-full h-full object-cover" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="text-xs font-black text-slate-900 truncate md:group-hover:text-rose-600 uppercase tracking-tight">{item.name}</h4>
                                <div className="flex items-center gap-1.5 truncate mt-0.5">
                                    {item.team === 'Professional Player' ? (
                                        <div className="flex items-center gap-1">
                                            <img src={`https://flagcdn.com/w20/${getCountryCode(item.nationality)}.png`} className="w-3 h-2.5 object-cover rounded-[1px]" alt="" />
                                            <span className="text-[9px] text-slate-400 font-bold uppercase">{item.nationality === 'Korea Republic' ? 'South Korea' : (item.nationality || 'Professional')}</span>
                                        </div>
                                    ) : (
                                        <span className="text-[9px] text-slate-400 font-bold uppercase truncate">{item.team}</span>
                                    )}
                                    {item.position && (
                                        <>
                                            <span className="text-slate-200 text-[8px] font-black">•</span>
                                            <span className="text-[9px] font-black text-rose-400 uppercase">{item.position}</span>
                                        </>
                                    )}
                                </div>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="w-12 h-10 rounded-lg overflow-hidden bg-gray-200 shrink-0 flex items-center justify-center">
                                <img src={item.image} alt="" className="w-full h-full object-cover" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="text-[11px] font-bold text-slate-900 line-clamp-2 leading-tight md:group-hover:text-rose-600">{item.title}</h4>
                                {displayName && (
                                    <div className="flex items-center gap-1.5 mt-1">
                                        <span className="text-[8px] font-black text-rose-500 uppercase tracking-widest block">{displayName}</span>
                                    </div>
                                )}
                              </div>
                            </>
                          )}
                          <ChevronRight size={14} className="text-gray-300 md:group-hover:text-rose-500" />
                        </button>
                    );
                  })}
                </div>
                
                {totalPages > 1 && (
                  <div className="p-3 bg-gray-50/80 border-t border-gray-100 flex items-center justify-between">
                    <button 
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(prev => prev - 1)}
                      className="p-1.5 rounded-lg md:hover:bg-white text-slate-400 md:hover:text-rose-500 disabled:opacity-30 disabled:md:hover:bg-transparent disabled:md:hover:text-slate-400 transition-all"
                    >
                      <ChevronLeft size={16} strokeWidth={3} />
                    </button>
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                      {currentPage} / {totalPages}
                    </span>
                    <button 
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(prev => prev + 1)}
                      className="p-1.5 rounded-lg md:hover:bg-white text-slate-400 md:hover:text-rose-500 disabled:opacity-30 disabled:md:hover:bg-transparent disabled:md:hover:text-slate-400 transition-all"
                    >
                      <ChevronRight size={16} strokeWidth={3} />
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="py-12 text-center">
                <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest">{t(lang, 'noResultsFound')}</p>
              </div>
            )}
        </div>
      )}

      {!query && (
        <div className="overflow-hidden">
            <div className="p-1 pb-5">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <TrendingUp size={16} className="text-rose-500" />
                        <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest">{t(lang, 'trendingIntel')}</h3>
                    </div>
                    <button 
                        onClick={() => window.navigateTo('/feed')}
                        className="text-[10px] font-black text-rose-500 uppercase tracking-widest md:hover:underline"
                    >
                        {t(lang, 'viewAll')}
                    </button>
                </div>
                
                <div className="space-y-4">
                    {isPopularLoading ? (
                        [1,2,3,4,5].map(i => <div key={i} className="animate-pulse flex gap-3">
                            <div className="w-14 h-14 bg-gray-200 rounded-xl"></div>
                            <div className="flex-1 space-y-2 py-1">
                                <div className="h-2 bg-gray-200 rounded w-3/4"></div>
                                <div className="h-2 bg-gray-200 rounded w-1/2"></div>
                            </div>
                        </div>)
                    ) : popularNews.length > 0 ? (
                        popularNews.map((news) => {
                            const sourceLower = news.source?.toLowerCase() || '';
                            const isPartnerSource = sourceLower.includes('bbc') || sourceLower.includes('teamtalk') || sourceLower.includes('si') || sourceLower.includes('sports illustrated');
                            const isWeeklyMagazine = sourceLower === 'magazine';
                            const isWebtoon = sourceLower === 'webtoon' && news.type === 'webtoon';
                            const isUserPost = !!news.user_id;
                            // Fix: Use 'news' instead of 'item' as it is the mapping variable
                            const displayName = isPartnerSource ? 'XLEVEN' : isWeeklyMagazine ? 'Magazine' : isWebtoon ? 'Webtoon' : (isUserPost ? news.author?.name : (news.source || null));

                            const thumbnail = news.image || news.background || (isUserPost ? news.author?.avatar : news.sourceIcon) || `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName || 'S')}&background=random`;

                            return (
                                <button 
                                    key={news.id} 
                                    onClick={() => onNavigateToNews(news)}
                                    className="w-full flex gap-3 text-left group items-start"
                                >
                                    <div className="w-14 h-14 rounded-xl overflow-hidden shrink-0 border border-gray-100 bg-white flex items-center justify-center">
                                        <img 
                                                src={thumbnail} 
                                                alt="" 
                                                className={`w-full h-full object-cover grayscale-[0.3] md:group-hover:grayscale-0 transition-all ${isUserPost ? 'scale-[1.3]' : ''}`} 
                                            />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h4 className="text-[11px] font-black text-slate-800 line-clamp-2 leading-tight md:group-hover:text-rose-600 transition-colors uppercase tracking-tight">
                                            {news.title}
                                        </h4>
                                        <div className="flex items-center gap-2 mt-1.5">
                                            {displayName && (
                                                <div className="flex items-center gap-1">
                                                    <span className="text-[8px] font-bold text-slate-400 uppercase truncate max-w-[80px]">{displayName}</span>
                                                </div>
                                            )}
                                            {displayName && <span className="w-0.5 h-0.5 rounded-full bg-slate-300"></span>}
                                            <div className="flex items-center gap-1 text-rose-500">
                                                <Eye size={8} />
                                                <span className="text-[8px] font-black tabular-nums">Hot</span>
                                            </div>
                                        </div>
                                    </div>
                                </button>
                            );
                        })
                    ) : (
                        <p className="text-[10px] text-slate-300 text-center py-4 font-bold uppercase tracking-widest">{t(lang, 'noTrendingData')}</p>
                    )}
                </div>
            </div>
        </div>
      )}

      <div className="mt-auto">
        <AppFooter lang={lang} className="py-8 border-none bg-transparent" />
      </div>
    </div>
  );
};
